"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import qrCode from "@/app/assets/images/icm-3.0/floating-download/qr-code.png";
import appStoreIcon from "@/app/assets/images/icm-3.0/floating-download/app-store.png";
import playStoreIcon from "@/app/assets/images/icm-3.0/floating-download/play-store.png";
import { cn } from "@/lib/utils";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";

const APPLE_APP_URL = process.env.ICM_APPLE_APP_URL || "#";
const PLAYSTORE_URL = process.env.ICM_PLAYSTORE_URL || "#";

/**
 * Fixed bottom-right “Scan to Download” chip.
 * Collapsed by default; expands on hover and on click (pinned until click-outside).
 */
const FloatingAppDownload = () => {
    const [expanded, setExpanded] = useState(false);
    const [pinned, setPinned] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!pinned) return;

        const onPointerDown = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setPinned(false);
                setExpanded(false);
            }
        };

        document.addEventListener("pointerdown", onPointerDown);
        return () => document.removeEventListener("pointerdown", onPointerDown);
    }, [pinned]);

    const expand = () => setExpanded(true);
    const collapseIfUnpinned = () => {
        if (!pinned) setExpanded(false);
    };

    const pinOpen = () => {
        setPinned(true);
        setExpanded(true);
    };

    return (
        <div
            ref={rootRef}
            className="pointer-events-none fixed bottom-5 right-5 z-[110] flex items-end justify-end md:bottom-6 md:right-6"
        >
            <div
                className={cn(
                    "pointer-events-auto origin-bottom-right overflow-hidden bg-[#262626] text-white shadow-[0_4px_15px_0_rgba(0,0,0,0.15)] motion-reduce:transition-none",
                    "transition-[width,height,border-radius,padding] duration-300 ease-out",
                    expanded
                        ? "flex h-[163px] w-[min(367px,calc(100vw-2.5rem))] items-start gap-4 rounded-2xl p-[11px]"
                        : "flex h-[66px] w-[152px] cursor-pointer items-center gap-2 rounded-[var(--radius-m)] p-2",
                )}
                onPointerEnter={expand}
                onPointerLeave={collapseIfUnpinned}
                onClick={pinOpen}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (expanded && pinned) {
                            setPinned(false);
                            setExpanded(false);
                        } else {
                            pinOpen();
                        }
                    }
                    if (event.key === "Escape") {
                        setPinned(false);
                        setExpanded(false);
                    }
                }}
                role={expanded ? undefined : "button"}
                tabIndex={expanded ? undefined : 0}
                aria-expanded={expanded}
                aria-label="Scan to download the InCred Money app"
            >
                <Image
                    src={qrCode}
                    alt=""
                    width={141}
                    height={141}
                    className={cn(
                        "shrink-0 object-cover motion-reduce:transition-none",
                        "transition-[width,height,border-radius] duration-300 ease-out",
                        expanded ? "size-[141px] rounded-lg" : "size-[50px] rounded",
                    )}
                />

                {expanded ? (
                    <div className="flex w-[188px] min-w-0 flex-col items-start gap-5 pt-[3px]">
                        <p className="h-11 w-full text-base font-semibold leading-[22px]">
                            Access every opportunity in one app!
                        </p>
                        <div className="flex w-full flex-col items-start gap-2">
                            <p className="text-sm leading-5 text-white/75">
                                <span className="font-regular">Download </span>
                                <span className="font-semibold">InCred Money</span>
                            </p>
                            <div className="flex w-[102px] items-center justify-between">
                                <a
                                    href={APPLE_APP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Download on the App Store"
                                    className="size-8 shrink-0 overflow-hidden rounded-[7px] transition-opacity hover:opacity-80"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setAnalyticsEvent("Floating_App_Download", "App_Store");
                                    }}
                                >
                                    <Image
                                        src={appStoreIcon}
                                        alt=""
                                        width={32}
                                        height={32}
                                        className="size-8 object-cover"
                                    />
                                </a>
                                <span
                                    className="h-[33px] w-px shrink-0 bg-white"
                                    aria-hidden
                                />
                                <a
                                    href={PLAYSTORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Get it on Google Play"
                                    className="flex size-8 shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-80"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setAnalyticsEvent("Floating_App_Download", "Play_Store");
                                    }}
                                >
                                    <Image
                                        src={playStoreIcon}
                                        alt=""
                                        width={32}
                                        height={32}
                                        className="size-8 object-contain"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-base font-semibold leading-[22px]">
                        Scan to
                        <br />
                        Download
                    </p>
                )}
            </div>
        </div>
    );
};

export default FloatingAppDownload;
